import { KalturaLanguageListResponse } from '@kaltura-ott/ott-client-types/api/types';
import {
  downloadTranslation,
  DynamicDataTypeEnum,
  exportProjectFromPOEditor,
  getCacheValue,
  kuxUpdateConfig,
  languageList,
  setCacheValue,
} from '@kaltura-ott/tvpil';
import { action, computed, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container, singleton } from 'tsyringe';

import { errorHandling } from '../../utils/error-handling';
import { featureEnabler } from '../../utils/feature-enabler';
import { toast } from '../../utils/toast';
import type { AppParametersStore } from '../app/app-params-store';
import type { AppStore } from '../app/app-store';
import type { DmsStore } from '../dms/dms-store';
import type { LoginStore } from '../user/login-store';
import type { UserStore } from '../user/user-store';

import { languagePlayerSet } from './localization-services';
import { formattedLanguageList, Language, filterLanguageListByDmsTranslations } from './localization.helper';

export enum LanguageKeyEnum {
  display = DynamicDataTypeEnum.language,
  audio = DynamicDataTypeEnum.audioLanguage,
  subtitle = DynamicDataTypeEnum.subtitleLanguage,
}
type KuxTranslation = { [key: string]: string };

const offlineCheckTimeoutMs = 1 * 60 * 1000; // 1 min
const CACHE_TRANSLATION = 'translation';
const CACHE_LOGIN_LANG = 'CACHE_LOGIN_LANG';
const NO_SUBTITLE_VALUE = '';
/**
 * @category Store
 */
@singleton()
export class LocalizationStore {
  static moduleName = 'LocalizationStore';

  defaultLanguage = '';
  languagesList: Language[] = [];

  static anonymousLanguage: string; // web need access to this field because web app reloads during log in on some envs

  // A three-digit language code is needed for network requests in Phoenix
  appLanguage = '';
  @observable displayLanguage = '';
  @observable audioLanguage?: string;
  @observable subTitleLanguage?: string;
  @observable latinLanguage?: string;
  @observable.ref translation: KuxTranslation = {};

  private offlineCheckTimeout?: NodeJS.Timeout;

  constructor() {}

  async init(): Promise<void> {
    const loginStore = container.resolve<LoginStore>('loginStore');

    if (!loginStore.isAnonymous) {
      LocalizationStore.anonymousLanguage = '';
    }
    await this.getLanguageList();
    await this.setupLanguages();
    if (this.displayLanguage) {
      const { translation } = await this.fetchCurrentTranslation(this.displayLanguage);
      this.saveTranslation(translation);

      // @ts-expect-error TODO Fix
      this.appLanguage = this.getOriginCode(this.displayLanguage);
      kuxUpdateConfig({ language: this.appLanguage });
    }

    if (!this.offlineCheckTimeout && featureEnabler.variation('DOWNLOAD_TRANSLATION_FILE', false)) {
      this.offlineCheckTimeout = setTimeout(() => {
        this.checkOfflineTranslation();
      }, offlineCheckTimeoutMs);
    }
  }

  private async checkOfflineTranslation() {
    const offlineTranslation = await getCacheValue(CACHE_TRANSLATION);
    if (!offlineTranslation && this.translation && Object.keys(this.translation).length) {
      setCacheValue(CACHE_TRANSLATION, JSON.stringify(this.translation), 7 * 24 * 60 * 60 * 1000);
    }
  }

  async initOffline() {
    const offlineTranslation = await getCacheValue(CACHE_TRANSLATION);
    if (offlineTranslation) {
      this.translation = JSON.parse(offlineTranslation as string);
    }
  }

  @action.bound
  @errorHandling()
  @toast('setDisplayLanguage')
  async setDisplayLanguage(value: string) {
    await this.setLanguage(DynamicDataTypeEnum.language, value);
    const appStore = container.resolve<AppStore>('appStore');

    await appStore?.rebootApp(true);
  }

  @action.bound
  @errorHandling()
  @toast('setAudioLanguage')
  async setAudioLanguage(value: string) {
    await this.setLanguage(DynamicDataTypeEnum.audioLanguage, value);
  }

  @action.bound
  @errorHandling()
  @toast('setSubTitleLanguage')
  async setSubTitleLanguage(value: string) {
    await this.setLanguage(DynamicDataTypeEnum.subtitleLanguage, value);
  }

  languageNameByCode = (code: string) => {
    return computed(() => {
      return this.languagesList.find((item) => item.code === code)?.name || '';
    }).get();
  };

  // deprecated
  languageNativeNameByCode = (code: string) => {
    return computed(() => {
      const foundCurrentCode = this.languagesList.find((item) => item.code === code);
      return foundCurrentCode?.settingsNativeName || foundCurrentCode?.nativeName || '';
    }).get();
  };

  languageListForKey = (key: LanguageKeyEnum): Language[] => {
    return computed(() => {
      const code = this.getActiveCodeByLanguageKey(key);
      const list = [...this.languagesList];
      if (key === LanguageKeyEnum.subtitle) {
        list.push({
          id: 0,
          name: '',
          code: '',
          direction: 'none',
          isDefault: false,
          nativeName: '',
          settingsNativeName: '',
          originCode: '',
          latinCode: '',
        });
      }

      list.sort((x, y) => {
        return x.code == code ? -1 : y.code == code ? 1 : 0;
      });
      return list;
    }).get();
  };

  activeLanguageByKey = (key: LanguageKeyEnum) => {
    return computed(() => {
      const code = this.getActiveCodeByLanguageKey(key);
      return this.languageListForKey(key).find((lang) => lang.code === code);
    }).get();
  };

  getOriginCode = (code: string) => {
    return this.languagesList.find((lang) => lang.code === code)?.originCode;
  };

  private checkLanguageCode = (codeAlfa2: string) => {
    return !!this.languagesList.find((lang) => lang.code === codeAlfa2);
  };

  /**
   * check if the language has changed after login
   */
  checkLoginLanguage = (): undefined | boolean => {
    const userStore = container.resolve<UserStore>('userStore');

    const currentLanguage = userStore?.getFromDynamicData(DynamicDataTypeEnum.language);
    if (this.displayLanguage !== currentLanguage) {
      return true;
    }
  };

  private fetchCurrentTranslation = async (
    language: string,
  ): Promise<{ language: string; translation: KuxTranslation }> => {
    const appParametersStore = container.resolve<AppParametersStore>('appParamsStore');
    const dmsStore = container.resolve<DmsStore>('dmsStore');

    const { poEditorToken, projectId, proxy, exportProjectPoeditorAPI } = appParametersStore?.params?.poEditor || {};
    if (poEditorToken && projectId && exportProjectPoeditorAPI) {
      console.log(`Get translation from POEditor, project id: ${projectId}`);
      const translation = (await lastValueFrom(
        exportProjectFromPOEditor({
          proxy,
          language,
          projectId,
          poEditorToken,
          exportProjectPoeditorAPI,
        }),
      )) as KuxTranslation;

      return { language, translation };
    } else {
      const translationLinks = dmsStore?.dms?.translationLinks;
      const selectedLanguageAlfa3 = this.getOriginCode(language);
      const url = translationLinks ? translationLinks[`dictionary_url_${selectedLanguageAlfa3}`] : undefined;
      try {
        if (!url) {
          throw new Error('language url is not defined');
        }
        const translation = (await lastValueFrom(downloadTranslation(`${url}`, proxy))) as KuxTranslation;

        return { language, translation };
      } catch (e) {
        if (!this.translation && translationLinks) {
          // Load default language
          const url = translationLinks[`dictionary_url`];
          const translation = await lastValueFrom(downloadTranslation(url, proxy));
          this.translation = translation as KuxTranslation;
        }
        throw new Error('UNABLE_TO_SWITCH_LANGUAGE');
      }
    }
  };

  private saveTranslation(translation: KuxTranslation | undefined) {
    if (translation) {
      this.translation = translation;
    }
  }

  private async setLanguage(key: DynamicDataTypeEnum, value: string) {
    let protectedLanguage = value;
    let protectedTranslation;
    const loginStore = container.resolve<LoginStore>('loginStore');
    const userStore = container.resolve<UserStore>('userStore');

    if (key === DynamicDataTypeEnum.language) {
      const { language, translation } = await this.fetchCurrentTranslation(value);
      protectedLanguage = language;
      protectedTranslation = translation;
    }
    if (loginStore?.isAnonymous) {
      this.saveTranslation(protectedTranslation);
      LocalizationStore.anonymousLanguage = protectedLanguage;
      setCacheValue(CACHE_LOGIN_LANG, protectedLanguage);
      await this.setupLanguages();
    } else {
      await languagePlayerSet({ key, value: protectedLanguage });
      this.saveTranslation(protectedTranslation);
      await userStore?.loadUserData();
      await this.setupLanguages();
    }
  }

  private getLanguage(languageCode: string, backupLanguage: string) {
    return this.checkLanguageCode(languageCode) ? languageCode : backupLanguage;
  }

  private getLanguageByKey(key: DynamicDataTypeEnum, backupLanguage: string) {
    const userStore = container.resolve<UserStore>('userStore');

    const langFromDynamicData = userStore?.getFromDynamicData(key);

    if (key === DynamicDataTypeEnum.subtitleLanguage && langFromDynamicData === '') {
      return langFromDynamicData;
    }

    if (!langFromDynamicData) {
      return backupLanguage;
    }

    return this.getLanguage(langFromDynamicData, backupLanguage);
  }

  private async setupLanguages() {
    const loginStore = container.resolve<LoginStore>('loginStore');
    const userStore = container.resolve<UserStore>('userStore');

    if (loginStore?.isAnonymous) {
      LocalizationStore.anonymousLanguage =
        LocalizationStore.anonymousLanguage ||
        this.getLanguage(await getCacheValue(CACHE_LOGIN_LANG), this.defaultLanguage);

      this.displayLanguage = LocalizationStore.anonymousLanguage;
      this.audioLanguage = this.displayLanguage;
      this.subTitleLanguage = NO_SUBTITLE_VALUE;
      this.latinLanguage = this.languagesList?.find((language) => language.code === this.displayLanguage)?.latinCode;
    } else {
      const userLanguage = this.getLanguageByKey(
        DynamicDataTypeEnum.language,
        this.getLanguage(await getCacheValue(CACHE_LOGIN_LANG), this.defaultLanguage),
      );

      if (userLanguage !== userStore?.getFromDynamicData(DynamicDataTypeEnum.language)) {
        // store language for very first login, when it not stored in user's dynamic data
        await this.setLanguage(DynamicDataTypeEnum.language, userLanguage);
      }
      this.displayLanguage = userLanguage;
      this.audioLanguage = this.getLanguageByKey(DynamicDataTypeEnum.audioLanguage, this.displayLanguage);
      this.subTitleLanguage = this.getLanguageByKey(DynamicDataTypeEnum.subtitleLanguage, NO_SUBTITLE_VALUE);

      this.latinLanguage = this.languagesList?.find((language) => language.code === this.displayLanguage)?.latinCode;
    }
  }

  public async updateLanguages(displayLanguage: string, audioLanguage: string, subTitleLanguage: string) {
    this.displayLanguage = displayLanguage;
    this.audioLanguage = audioLanguage;
    this.subTitleLanguage = subTitleLanguage;

    const promises = [];
    promises.push(languagePlayerSet({ key: DynamicDataTypeEnum.language, value: displayLanguage }));
    promises.push(languagePlayerSet({ key: DynamicDataTypeEnum.audioLanguage, value: audioLanguage }));
    promises.push(languagePlayerSet({ key: DynamicDataTypeEnum.subtitleLanguage, value: subTitleLanguage }));
    await Promise.all(promises);

    // @ts-expect-error TODO Fix
    this.appLanguage = this.getOriginCode(this.displayLanguage);
    kuxUpdateConfig({ language: this.appLanguage });
  }

  public getLanguageList = async () => {
    const dmsStore = container.resolve<DmsStore>('dmsStore');

    const translationLinks = dmsStore?.dms?.translationLinks;
    if (!translationLinks) {
      return;
    }

    const result: KalturaLanguageListResponse = await lastValueFrom(languageList());
    if (result instanceof Error) {
      throw result;
    }
    const formattedLanguages = formattedLanguageList(
      result.objects,
      dmsStore?.dms?.playerLanguageCodes || [],
      dmsStore?.dms?.appLanguageCodes || {},
    );
    this.languagesList = filterLanguageListByDmsTranslations(formattedLanguages, translationLinks);
    this.defaultLanguage = this.languagesList?.find((language) => language.isDefault)?.code as string;
  };

  private getActiveCodeByLanguageKey(key: LanguageKeyEnum) {
    switch (key) {
      case LanguageKeyEnum.display:
        return this.displayLanguage;
      case LanguageKeyEnum.subtitle:
        return this.subTitleLanguage;
      case LanguageKeyEnum.audio:
        return this.audioLanguage;
      default:
        throw new Error(`${key} is not LanguageKeyEnum`);
    }
  }
}
